import { Link } from "react-router-dom";
import type { GroupView } from "@/services/types";
import { COMMISSION_LABEL, DIVIDEND_TIMING_LABEL } from "@/config/groups";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-xs text-foreground-500">{label}</span>
      <span className="text-right text-sm font-medium text-foreground-900">{value}</span>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="border-b border-background-100 pb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-500">{title}</h4>
      <div className="pt-1">{children}</div>
    </div>
  );
}

interface OverviewTabProps {
  group: GroupView;
  isViewer: boolean;
}

export function OverviewTab({ group, isViewer }: OverviewTabProps) {
  return (
    <div className="space-y-4">
      <Card
        title="Configuration"
        subtitle="Full read-only view of this group's setup."
        actions={
          group.status === "draft" && !isViewer ? (
            <Link to={`/groups/${group.id}/edit`}>
              <Button size="sm" variant="outline" icon={<i className="ri-pencil-line" />}>
                Edit group
              </Button>
            </Link>
          ) : null
        }
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
          <Group title="Basic details">
            <Row label="Group name" value={group.name} />
            <Row label="Group code" value={<span className="tnum">{group.groupCode}</span>} />
            <Row label="Monthly installment" value={<CurrencyDisplay amount={group.monthlyInstallment} hideSymbol />} />
            <Row label="Members" value={group.memberCount} />
            <Row label="Duration" value={`${group.memberCount} months`} />
            <Row label="Chit value" value={<CurrencyDisplay amount={group.chitValue} hideSymbol />} />
          </Group>
          <Group title="Schedule">
            <Row label="Start date" value={formatDate(group.startDate)} />
            <Row label="Auction day" value={`${group.auctionDay}th of month`} />
            <Row label="Due day" value={`${group.dueDay}th of month`} />
          </Group>
          <Group title="Organizer & commission">
            <Row label="Reserved organizer cycle" value={`Cycle ${group.reservedCycle}`} />
            <Row label="Commission" value={COMMISSION_LABEL[group.commissionType]} />
            <Row label="Commission value" value={group.commissionType === "none" ? "—" : group.commissionType === "fixed" ? <CurrencyDisplay amount={group.commissionValue} hideSymbol /> : `${group.commissionValue}%`} />
          </Group>
          <Group title="Bidding rules">
            <Row label="Min discount" value={group.minDiscountPct != null ? `${group.minDiscountPct}%` : "No cap"} />
            <Row label="Max discount" value={group.maxDiscountPct != null ? `${group.maxDiscountPct}%` : "No cap"} />
            <Row label="Bid step" value={<CurrencyDisplay amount={group.bidStep} hideSymbol />} />
            <Row label="Overdue blocked" value={group.blockOverdueBidding ? "Yes" : "No"} />
          </Group>
          <Group title="Dividend & penalty">
            <Row label="Dividend timing" value={DIVIDEND_TIMING_LABEL[group.dividendTiming]} />
            <Row label="Grace period" value={`${group.gracePeriodDays} days`} />
            <Row label="Late fee" value={`${group.lateFeeIsPercent ? `${group.lateFee}%` : <CurrencyDisplay amount={group.lateFee} hideSymbol />}${group.lateFeePerDay ? " / day" : ""}`} />
          </Group>
          {group.cancelledReason ? (
            <div className="md:col-span-2">
              <p className="text-xs text-foreground-500">Cancellation reason</p>
              <p className="mt-0.5 text-sm text-foreground-800">{group.cancelledReason}</p>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}